/* Storage utility functions for Magni Sign-in/out Application */

// Import constants
import { LOCATIONS } from './constants.ts';

/**
 * Sets the current application location with multiple fallbacks
 * @param {string} location - Location name
 * @returns {boolean} - Success status
 */
const setAppLocation = (location) => {
  // First update the global variable (this will always work in the current session)
  window.MAGNI_CURRENT_LOCATION = location;
  
  // Then try localStorage (may fail in some environments)
  try {
    localStorage.setItem('magniLocation', location);
    console.log(`Location set in localStorage: ${location}`);
  } catch (e) {
    console.log('Could not set location in localStorage: ', e);
  }
  
  // Also try sessionStorage (another option that might work)
  try {
    sessionStorage.setItem('magniLocation', location);
    console.log(`Location set in sessionStorage: ${location}`);
  } catch (e) {
    console.log('Could not set location in sessionStorage: ', e);
  }
  
  // Also try setting a cookie (yet another fallback)
  try {
    document.cookie = `magniLocation=${location}; max-age=31536000; path=/`;
    console.log(`Set cookie: magniLocation=${location}`);
  } catch (e) {
    console.log('Could not set cookie: ', e);
  }
  
  return true; // Operation considered successful if global variable is set
};

/**
 * Gets the current application location from various storage options
 * @returns {string} - Current location
 */
const getAppLocation = () => {
  // Check all possible storage locations
  let location;
  
  // First check global variable (most reliable for current session)
  if (window.MAGNI_CURRENT_LOCATION) {
    console.log(`Retrieved location from global variable: ${window.MAGNI_CURRENT_LOCATION}`);
    return window.MAGNI_CURRENT_LOCATION;
  }
  
  // Try localStorage
  try {
    location = localStorage.getItem('magniLocation');
    if (location && LOCATIONS.includes(location)) {
      console.log(`Retrieved location from localStorage: ${location}`);
      window.MAGNI_CURRENT_LOCATION = location; // Update global var
      return location;
    }
  } catch (e) {
    console.log('Could not get location from localStorage: ', e);
  }
  
  // Try sessionStorage
  try {
    location = sessionStorage.getItem('magniLocation');
    if (location && LOCATIONS.includes(location)) {
      console.log(`Retrieved location from sessionStorage: ${location}`);
      window.MAGNI_CURRENT_LOCATION = location; // Update global var
      return location;
    }
  } catch (e) {
    console.log('Could not get location from sessionStorage: ', e);
  }
  
  // Try cookies
  try {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith('magniLocation=')) {
        location = cookie.substring('magniLocation='.length);
        if (location && LOCATIONS.includes(location)) {
          console.log(`Retrieved location from cookie: ${location}`);
          window.MAGNI_CURRENT_LOCATION = location; // Update global var
          return location;
        }
      }
    }
  } catch (e) {
    console.log('Could not get location from cookies: ', e);
  }
  
  // Default to Detroit if all else fails
  console.log('No location found, defaulting to Detroit');
  window.MAGNI_CURRENT_LOCATION = 'Detroit';
  return 'Detroit';
};

/**
 * Loads saved users from localStorage
 * @returns {Array} - Array of user objects
 */
const loadSavedUsers = () => {
  try {
    const savedUsers = localStorage.getItem('magniUsers');
    if (savedUsers) {
      const parsedUsers = JSON.parse(savedUsers);
      
      // Convert string dates back to Date objects
      parsedUsers.forEach(user => {
        user.signIns.forEach(signIn => {
          signIn.date = new Date(signIn.date);
        });
      });
      
      return parsedUsers;
    }
  } catch (e) {
    console.error('Error loading saved users:', e);
  }
  return [];
};

/**
 * Saves users to localStorage
 * @param {Array} users - Array of user objects to save
 * @returns {boolean} - Success status
 */
const saveUsersStorage = (users) => {
  try {
    localStorage.setItem('magniUsers', JSON.stringify(users));
    return true;
  } catch (e) {
    console.error('Error saving users:', e);
    return false;
  }
};

// Export all functions
export {
  setAppLocation,
  getAppLocation,
  loadSavedUsers,
  saveUsersStorage
};